/**
 * ==========================================================================
 *  The muurikka out on the kivetys, and the man cooking breakfast on it.
 *
 *  Rendering and nothing else, like the fire and the plumes: `MuurikkaSpec` is
 *  never read by anything in `src/sim`, the fingerprint never sees it, and not
 *  one box in here is a collider. It can therefore be re-shaped, re-cooked or
 *  moved along the wall without retiring a leaderboard time - see
 *  `src/config/CLAUDE.md`.
 *
 *  Everything but the cook is built in the pan's own frame, where **local +Z
 *  is the side the knobs are on, which is the side the cook is on**. That is
 *  worth stating once because it settles three separate things at a stroke:
 *  the burner's controls are within reach of the only man using them, the
 *  lasta lies with its handle towards him, and `MuurikkaSpec.rotationY` turns
 *  all of it as one piece.
 *
 *  The class holds the three things the aamupala minigame moves afterwards:
 *  the food on the plate, the spatula, and the fist the spatula pivots at.
 *  `build` hands the pan and the cook back for the caller to place - the cook
 *  is a standing figure like any other and is registered as one.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { LIMB_AXIS } from './People';
import { standerFigure } from './PeopleUpright';
import { ballGeometry } from './Figure';
import type { SceneAssets } from './SceneAssets';
import { mulberry32 } from '../utils/prng';
import type { AamupalaView } from '../world/Aamupala';
import type { MuurikkaSpec } from '../world/Level';

export class Muurikka {
  /**
   * The pan's dressing, in the two groups the aamupala minigame moves.
   *
   * `foodGroup` is everything on the plate, parented at the plate's own centre
   * so that stirring it is one rotation rather than nine; `lastaGroup` is the
   * spatula, pivoted at the cook's fist so that a sweep across the pan leaves
   * the handle where his hand is.
   */
  private foodGroup: THREE.Group | null = null;
  private lastaGroup: THREE.Group | null = null;
  private cookGrip: THREE.Object3D | null = null;
  private readonly gripPosition = new THREE.Vector3();

  constructor(private readonly assets: SceneAssets) {}

  /**
   * One frame of the minigame: the lasta sweeps and the food turns under it.
   *
   * The lasta only sweeps while the hand is going, and the food only moves
   * while the lasta is on it. Everything else - waiting to be chosen from,
   * waiting for Kimmo to stop being sick, and the whole of the run before
   * anybody comes to the grill - is a pan sitting still, which is what a pan
   * full of food does.
   */
  setAamupala(view: AamupalaView | null): void {
    const food = this.foodGroup;
    const lasta = this.lastaGroup;
    if (!food || !lasta) return;

    const mixing = view?.phase === 'mixing';
    const sweep = mixing ? Math.sin(view.progress * Math.PI * 6) : 0;
    lasta.rotation.y = sweep * 0.5;
    food.rotation.y = mixing ? view.progress * Math.PI * 1.2 : 0;
    // And a hair of bounce, so the food is being *turned* rather than spun on a
    // turntable. Two millimetres: any more and the eggs leave the plate.
    food.position.y = mixing ? Math.abs(sweep) * 0.002 : 0;
  }

  /** The spatula pivots at the cook's fist even when he turns away from the pan. */
  followGrip(): void {
    if (!this.cookGrip || !this.lastaGroup?.parent) return;
    this.cookGrip.getWorldPosition(this.gripPosition);
    this.lastaGroup.position.copy(this.lastaGroup.parent.worldToLocal(this.gripPosition));
  }

    /**
     * The muurikka out on the kivetys, and the man cooking breakfast on it.
     *
     * Rendering and nothing else, like the fire and the plumes: `MuurikkaSpec`
     * is never read by anything in `src/sim`, the fingerprint never sees it, and
     * not one box in here is a collider. It can therefore be re-shaped, re-cooked
     * or moved along the wall without retiring a leaderboard time - see
     * `src/config/CLAUDE.md`.
     *
     * Everything but the cook is built in the pan's own frame, where **local +Z
     * is the side the knobs are on, which is the side the cook is on**. That is
     * worth stating once because it settles three separate things at a stroke:
     * the burner's controls are within reach of the only man using them, the
     * lasta lies with its handle towards him, and `MuurikkaSpec.rotationY` turns
     * all of it as one piece.
     */
    build(spec: MuurikkaSpec): { pan: THREE.Group; cook: THREE.Object3D } {

      const r = spec.radius;
      const top = spec.height;
      const group = new THREE.Group();
      group.name = spec.name;
      group.position.set(spec.center[0], 0, spec.center[1]);
      group.rotation.y = spec.rotationY;

      const steel = this.assets.material('metal');
      const black = this.assets.material('stove');
      const rng = mulberry32(0x4a17b2);

      // Three legs, splayed: they stand on a circle wider than the one they meet
      // the pan on, which is the whole of why a stand this top-heavy does not go
      // over when somebody leans on the rim. None of them is under the knobs.
      const footRadius = r * 0.84;
      const hipRadius = r * 0.48;
      const hipY = top - 0.24;
      const lean = hipRadius - footRadius;
      for (const turn of [1, 3, 5]) {
        const leg = new THREE.Group();
        leg.rotation.y = (turn * Math.PI) / 3;
        group.add(leg);
        const bar = new THREE.Mesh(
          new THREE.CylinderGeometry(0.022, 0.026, Math.hypot(hipY, lean), 6),
          black,
        );
        bar.position.set(0, hipY / 2, (footRadius + hipRadius) / 2);
        bar.rotation.x = Math.atan2(lean, hipY);
        leg.add(bar);
      }

      // The shelf between them - three legs, three sides. It is where the eggs
      // and the coffee pot live while the pan is full.
      const shelf = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.56, r * 0.56, 0.018, 3), black);
      shelf.position.y = top * 0.36;
      shelf.rotation.y = Math.PI / 6;
      group.add(shelf);

      // The burner housing: a bowl the ring sits inside, which is the black half
      // of the silhouette and the reason the plate reads as *on* something.
      const bowl = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.96, r * 0.5, 0.26, 20), black);
      bowl.position.y = top - 0.15;
      group.add(bowl);

      // And the plate: a shallow dish, wider at the rim than at the bed of it.
      // The flat top is the cooking surface, and it is the one surface out here
      // that catches the morning - hence steel rather than the sooty stove key.
      const plate = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 0.78, 0.08, 24), steel);
      plate.position.y = top - 0.04;
      group.add(plate);

      // A loop each side to carry it by, standing up off the rim the way one is
      // that has to be grabbed with a glove on.
      for (const side of [-1, 1]) {
        const handle = new THREE.Mesh(
          new THREE.TorusGeometry(r * 0.13, 0.012, 5, 9, Math.PI),
          steel,
        );
        handle.position.set(side * (r - 0.02), top - 0.01, 0);
        handle.rotation.y = Math.PI / 2;
        group.add(handle);
      }

      // Three knobs on the front of the housing, which is the side he is on.
      for (const offset of [-0.16, 0, 0.16]) {
        const knob = new THREE.Mesh(new THREE.CylinderGeometry(0.026, 0.03, 0.035, 8), steel);
        knob.position.set(offset, top - 0.15, r * 0.78);
        knob.rotation.x = Math.PI / 2;
        group.add(knob);
      }

      // The cook first, because the lasta is drawn between the plate and his
      // hand and cannot be placed until there is a hand to place it against.
      const { figure: cook, grip } = this.addCook(spec);
      // The pan's world matrix has to be current before the dressing is placed:
      // `addBreakfast` converts the cook's fist into the plate's own frame.
      group.updateMatrixWorld(true);
      this.addBreakfast(group, spec, rng, grip);
      return { pan: group, cook };
    }

    /**
     * What is on the plate: makkara, and eggs broken in beside it.
     *
     * Laid out from the seeded authoring generator rather than by hand, because
     * what a pan of food looks like is *scattered* and eight positions typed out
     * in a config file are eight positions that read as a grid. Nothing here is
     * ever stepped - it is drawn once at load, exactly like a texture.
     */
    addBreakfast(
      group: THREE.Object3D,
      spec: MuurikkaSpec,
      rng: () => number,
      grip: THREE.Object3D | null,
    ): void {
      const r = spec.radius;
      const surface = spec.height;
      const browned = this.assets.figureMaterial(0x8f4a26);
      const white = this.assets.figureMaterial(0xf1e9d8);
      const yolk = this.assets.figureMaterial(0xe0a125);
      const stalk = this.assets.figureMaterial(0x7a8f4a);
      const floret = this.assets.figureMaterial(0x3f6b2f);
      const fried = this.assets.figureMaterial(0x8a7a3e);

      // Everything on the plate goes in one group, pivoted on the plate's own
      // middle. That is what lets `setAamupala` stir it: a pan being worked is
      // its whole contents going round, and nine meshes each carrying their own
      // idea of where the middle is would be nine things drifting apart.
      const food = new THREE.Group();
      food.name = 'ruoka';
      group.add(food);
      this.foodGroup = food;

      // Sausages, on the half of the plate he is not currently working.
      const sausage = new THREE.CapsuleGeometry(0.021, 0.085, 2, 6);
      for (let i = 0; i < 9; i++) {
        const angle = rng() * Math.PI * 2;
        const reach = Math.sqrt(rng()) * r * 0.66;
        const mesh = new THREE.Mesh(sausage, browned);
        mesh.position.set(Math.sin(angle) * reach, surface + 0.021, Math.cos(angle) * reach);
        // Flat on the plate, then turned. A sausage stood on end is a candle.
        mesh.rotation.set(Math.PI / 2, rng() * Math.PI, 0, 'YXZ');
        food.add(mesh);
      }

      // And the eggs, which is what makes it breakfast rather than an evening.
      for (let i = 0; i < 3; i++) {
        const angle = rng() * Math.PI * 2;
        const reach = 0.2 + rng() * r * 0.4;
        const x = Math.sin(angle) * reach;
        const z = Math.cos(angle) * reach;
        const flat = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.06, 0.012, 9), white);
        flat.position.set(x, surface + 0.006, z);
        food.add(flat);
        const middle = new THREE.Mesh(ballGeometry(), yolk);
        middle.scale.set(0.055, 0.03, 0.055);
        middle.position.set(x, surface + 0.016, z);
        food.add(middle);
      }

      // Parsakaalia, because the ruokatiimi caters for everybody and the pan is
      // the only place in the yard where that is a problem. Each one is a stub of
      // stalk with three heads on it, which is what a floret is at this size and
      // from this distance - and it has to read as *not meat* in the half-second
      // a runner spends looking at the pan, so it is the only green out here.
      const stem = new THREE.CylinderGeometry(0.012, 0.014, 0.05, 6);
      const head = ballGeometry();
      for (let i = 0; i < 5; i++) {
        const angle = rng() * Math.PI * 2;
        const reach = Math.sqrt(rng()) * r * 0.6;
        const x = Math.sin(angle) * reach;
        const z = Math.cos(angle) * reach;
        const trunk = new THREE.Mesh(stem, stalk);
        trunk.position.set(x, surface + 0.025, z);
        trunk.rotation.set(0.5 + rng() * 0.4, rng() * Math.PI, 0, 'YXZ');
        food.add(trunk);
        for (let bud = 0; bud < 3; bud++) {
          const spread = 0.028;
          const at = (bud / 3) * Math.PI * 2 + rng();
          const bulb = new THREE.Mesh(head, floret);
          bulb.scale.setScalar(0.026 + rng() * 0.008);
          bulb.position.set(x + Math.sin(at) * spread, surface + 0.05, z + Math.cos(at) * spread);
          food.add(bulb);
        }
      }

      // And falafelia: browner than the parsakaali and rounder than everything
      // else, which is exactly the trap - at a glance across a hot plate a
      // falafel is a slice of makkara, and telling them apart in one second is
      // the whole minigame. See src/config/aamupala.ts.
      for (let i = 0; i < 7; i++) {
        const angle = rng() * Math.PI * 2;
        const reach = Math.sqrt(rng()) * r * 0.68;
        const ball = new THREE.Mesh(head, fried);
        // Squashed, because a falafel that has been on a plate is a patty.
        ball.scale.set(0.03, 0.021, 0.03);
        ball.position.set(Math.sin(angle) * reach, surface + 0.021, Math.cos(angle) * reach);
        food.add(ball);
      }

      // And the lasta, which is the one thing on the pan that is not food: blade
      // flat on the far half of the plate, handle running back up to the hand
      // that is holding it.
      //
      // Built from its two ends rather than from a length and an angle, and the
      // far end is asked of the cook rather than typed in here. That is what
      // makes "he is holding it" true by construction: turn him, move the pan,
      // change the reach of an arm, and the handle still finishes in his fist
      // instead of a centimetre out of it in a way nobody would think to check.
      const hand = new THREE.Vector3(0, surface + 0.32, r * 0.5);
      if (grip) group.worldToLocal(grip.getWorldPosition(hand));

      // Hung off a group pivoted *at the hand* rather than added to the pan
      // directly, so that sweeping the blade across the plate is one rotation
      // about the fist holding it. A lasta swung any other way is a lasta that
      // leaves the man's hand behind.
      const lasta = new THREE.Group();
      lasta.name = 'lasta';
      lasta.position.copy(hand);
      group.add(lasta);
      this.lastaGroup = lasta;

      const bladeAt = new THREE.Vector3(-r * 0.34, surface + 0.008, -r * 0.2).sub(hand);
      const run = bladeAt.clone().negate();
      const blade = new THREE.Mesh(this.assets.boxGeometry, this.assets.material('metal'));
      blade.scale.set(0.1, 0.008, 0.16);
      blade.position.copy(bladeAt);
      // Lying along the way the handle runs, because a spatula is not a coaster.
      blade.rotation.y = Math.atan2(run.x, run.z);
      lasta.add(blade);

      const shaft = new THREE.Mesh(
        new THREE.BoxGeometry(0.026, 0.026, run.length()),
        this.assets.material('wood'),
      );
      shaft.position.copy(bladeAt).addScaledVector(run, 0.5);
      shaft.quaternion.setFromUnitVectors(LIMB_AXIS, run.clone().normalize());
      lasta.add(shaft);
    }

    /**
     * The man at the pan.
     *
     * He stands behind it facing out over the plate, which is where somebody
     * cooking for a yard stands - and it is also the only side of it the runner
     * ever sees, so it is the side his face has to be on.
     *
     * Two things are posed here rather than in the level data. His arms come
     * forward and down, and his body and head follow Kimmo like the other awake
     * standers. The spatula follows his fist when he turns.
     *
     * Returns the hand the lasta goes in, which is the whole reason he is built
     * before the pan's own dressing is - see `addBreakfast`.
     */
    addCook(spec: MuurikkaSpec): { figure: THREE.Object3D; grip: THREE.Object3D | null } {
      const cook = spec.cook;
      const figure = standerFigure(this.assets, cook, 0);
      figure.name = cook.name;
      figure.position.set(cook.stand[0], 0, cook.stand[1]);
      figure.rotation.y = cook.rotationY;

      // Both arms out over the plate. Two bends of the same size rather than one
      // big one: a single hinge at the shoulder is a man pointing.
      let grip: THREE.Object3D | null = null;
      for (const name of ['kasi-vasen', 'kasi-oikea']) {
        const arm = figure.getObjectByName(name);
        if (!arm) continue;
        arm.rotation.x = -0.55;
        const elbow = arm.getObjectByName('kyynarpaa');
        if (elbow) elbow.rotation.x = -0.55;
        // The lasta goes in the hand that finishes over the middle of the plate,
        // which is the far one: the body is turned down the facade, so the near
        // arm swings out past the rim rather than over it.
        if (name === 'kasi-vasen') grip = arm.getObjectByName('kammen') ?? null;
      }

      this.cookGrip = grip;

      return { figure, grip };
    }
}
